import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import './NavLinks.css';

function NavLinks({ items = [], onLinkClick }) {
  const location = useLocation();

  // Normalize path: treat '/' and '/home' as the same
  const normalizePath = (path) => {
    if (!path || path === '/' || path === '/home') {
      return '/';
    }
    return path;
  };

  const handleNavClick = (e, targetPage, onClick, linkPath) => {
    // Extract path and hash from linkPath if it contains a hash
    let pathWithoutHash = linkPath;
    let hashFromPath = null;
    
    if (linkPath && linkPath.includes('#')) {
      const parts = linkPath.split('#');
      pathWithoutHash = parts[0];
      hashFromPath = parts[1];
    }
    
    const currentPath = normalizePath(location.pathname);
    const targetPath = normalizePath(pathWithoutHash);
    
    // Check if we're navigating to the same page
    const isSamePage = currentPath === targetPath;
    
    if (isSamePage) {
      // Prevent default navigation to avoid full page reload
      e.preventDefault();
      
      // Extract hash from various sources: href attribute, linkPath, or current hash
      const href = e.currentTarget.getAttribute('href') || '';
      const hashFromHref = href.includes('#') ? href.split('#')[1] : null;
      const hash = hashFromHref || hashFromPath || window.location.hash.slice(1);
      
      if (hash) {
        // Update URL hash without reload
        window.history.pushState(null, '', `${location.pathname}#${hash}`);
        
        // Scroll to the element
        setTimeout(() => {
          const element = document.getElementById(hash);
          if (element) {
            const headerOffset = 80; // Account for fixed header
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth'
            });
          }
        }, 100);
      } else {
        // No hash, just scroll to top
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }
      
      if (onClick) onClick();
      return;
    }
    
    // Different page - allow normal navigation
    // If navigating to home page, scroll to top instantly
    const isHomePage = !targetPage || 
                      !targetPage.slug || 
                      targetPage.slug === '' || 
                      targetPage.slug === 'home' ||
                      linkPath === '/' ||
                      linkPath === '/home' ||
                      (pathWithoutHash && normalizePath(pathWithoutHash) === '/');
    
    if (isHomePage) {
      window.scrollTo({
        top: 0,
        behavior: 'instant'
      });
    }
    if (onClick) onClick();
  };

  // Check if an external URL is actually an internal site URL
  const isInternalUrl = (url) => {
    if (!url) return false;
    // Internal URLs start with / or are relative paths
    // External URLs start with http://, https://, mailto:, tel:, etc.
    return url.startsWith('/') && !url.startsWith('//');
  };

  const renderNavItem = (item) => {
    const hasChildren = item.children && item.children.length > 0;
    
    if (hasChildren) {
      return (
        <li key={item.label} className="nav-item dropdown">
          <span className="nav-link" style={{ cursor: 'pointer' }}>{item.label}</span>
          <ul className="dropdown-menu">
            {item.children.map(child => {
              // Check if child has a hash in its slug or if we need to construct path with hash
              let childPath = child.targetPage ? `/${child.targetPage.slug || ''}` : null;
              // If the slug itself contains a hash, preserve it
              if (child.targetPage && child.targetPage.slug && child.targetPage.slug.includes('#')) {
                childPath = `/${child.targetPage.slug}`;
              }
              
              // Check if externalUrl is actually an internal link
              if (!child.targetPage && child.externalUrl && isInternalUrl(child.externalUrl)) {
                childPath = child.externalUrl;
              }
              
              return (
                <li key={child.label}>
                  {child.targetPage || (child.externalUrl && isInternalUrl(child.externalUrl)) ? (
                    <Link 
                      to={childPath} 
                      className="dropdown-item" 
                      onClick={(e) => handleNavClick(e, child.targetPage, onLinkClick, childPath)}
                    >
                      {child.label}
                    </Link>
                  ) : (
                    <a href={child.externalUrl} className="dropdown-item" onClick={onLinkClick}>
                      {child.label}
                    </a>
                  )}
                </li>
              );
            })}
          </ul>
        </li>
      );
    }

    // Check if item has a hash in its slug
    let itemPath = item.targetPage ? `/${item.targetPage.slug || ''}` : null;
    // If the slug itself contains a hash, preserve it
    if (item.targetPage && item.targetPage.slug && item.targetPage.slug.includes('#')) {
      itemPath = `/${item.targetPage.slug}`;
    }
    
    // Check if externalUrl is actually an internal link
    if (!item.targetPage && item.externalUrl && isInternalUrl(item.externalUrl)) {
      itemPath = item.externalUrl;
    }
    
    return (
      <li key={item.label} className="nav-item">
        {item.targetPage || (item.externalUrl && isInternalUrl(item.externalUrl)) ? (
          <Link 
            to={itemPath} 
            className="nav-link" 
            onClick={(e) => handleNavClick(e, item.targetPage, onLinkClick, itemPath)}
          >
            {item.label}
          </Link>
        ) : (
          <a href={item.externalUrl} className="nav-link" onClick={onLinkClick}>
            {item.label}
          </a>
        )}
      </li>
    );
  };

  if (!items || items.length === 0) {
    return null;
  }

  return (
    <ul className="navbar-nav">
      {items.map(renderNavItem)}
    </ul>
  );
}

export default NavLinks;
